import {
  endOfDay,
  endOfMonth,
  endOfTomorrow,
  endOfWeek,
  endOfYear,
  isSameDay,
  isValid,
  startOfDay,
  startOfMonth,
  startOfTomorrow,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { DateParam, useQueryParams, withDefault } from "use-query-params";
import { DatePreset } from "@/components/atoms/DateRangePicker/context";
import { useOperator } from "@/hooks";

type PresetName = Exclude<DatePreset, undefined>;

/**
 * Mirrors the ranges `DateRangePicker`'s Nav applies when a preset is clicked. Kept as thunks because
 * every one of them is relative to "now" — the whole point of storing the preset NAME rather than the
 * dates it resolved to is that "This Month" has to mean September in September and October in October.
 */
const PRESET_RANGES: Record<PresetName, () => [Date, Date]> = {
  today: () => [startOfDay(new Date()), endOfDay(new Date())],
  tomorrow: () => [startOfTomorrow(), endOfTomorrow()],
  thisWeek: () => [startOfWeek(new Date()), endOfWeek(new Date())],
  thisMonth: () => [startOfMonth(new Date()), endOfMonth(new Date())],
  thisYear: () => [startOfYear(new Date()), endOfYear(new Date())],
};

/**
 * Which preset, if any, a range currently represents. Day-granular on purpose: Nav's "Today" applies
 * `[new Date(), new Date()]` (the current time, twice) while our default applies start/end of day, and
 * both should read as "today".
 */
export const getDatePreset = (dateFrom?: Date, dateTo?: Date): DatePreset => {
  if (!dateFrom || !dateTo) return undefined;

  return (Object.keys(PRESET_RANGES) as PresetName[]).find((preset) => {
    const [from, to] = PRESET_RANGES[preset]();
    return isSameDay(dateFrom, from) && isSameDay(dateTo, to);
  });
};

/**
 * A preset is stored by name so it re-resolves against today's calendar; anything else is stored as
 * the literal range the operator picked.
 */
type StoredDateRange = { preset: PresetName } | { dateFrom: string; dateTo: string };

const storageKey = (operatorId: string) => `${operatorId}:bookings-dates`;

const readStoredRange = (operatorId: string): [Date, Date] | undefined => {
  try {
    const raw = window.localStorage.getItem(storageKey(operatorId));
    if (!raw) return undefined;

    const stored = JSON.parse(raw) as StoredDateRange;

    if ("preset" in stored) return PRESET_RANGES[stored.preset]?.();

    const dateFrom = new Date(stored.dateFrom);
    const dateTo = new Date(stored.dateTo);
    return isValid(dateFrom) && isValid(dateTo) ? [dateFrom, dateTo] : undefined;
  } catch {
    // Unreadable or hand-edited storage should fall back to Today, never break the page.
    return undefined;
  }
};

const writeStoredRange = (operatorId: string, dateFrom?: Date, dateTo?: Date) => {
  if (!dateFrom || !dateTo) return;

  const preset = getDatePreset(dateFrom, dateTo);
  const stored: StoredDateRange = preset ? { preset } : { dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString() };

  try {
    window.localStorage.setItem(storageKey(operatorId), JSON.stringify(stored));
  } catch {
    // Private-mode / quota failures just mean the choice doesn't outlive the tab.
  }
};

interface UseBookingDatesOptions {
  /**
   * Remember the operator's choice for next time. Opt-in because this hook is shared with the driver
   * Job History page, which has its own picker and should keep defaulting to today.
   */
  persist?: boolean;
}

export const useBookingDates = ({ persist = false }: UseBookingDatesOptions = {}) => {
  const { id: operatorId } = useOperator();

  /**
   * Resolved synchronously, once, before the first render — not in an effect.
   *
   * Every Bookings caller of this hook falls back to its own `withDefault` whenever the URL carries no
   * date params, so a default that arrived a tick later would leave `BookingControls` and
   * `useGetBookingsParams` briefly disagreeing, and cost a second bookings fetch on every page load.
   */
  const [defaultDates] = useState<[Date, Date]>(
    () => (persist ? readStoredRange(operatorId) : undefined) ?? [startOfDay(new Date()), endOfDay(new Date())]
  );

  const [selectedDates, setSelectedDates] = useQueryParams({
    dateFrom: withDefault(DateParam, defaultDates[0]),
    dateTo: withDefault(DateParam, defaultDates[1]),
  });

  /**
   * Persisted from the setter rather than an effect on `selectedDates`: an effect would fire on mount
   * with the pre-restore value and overwrite the very preference it had just read back.
   */
  const setDates = useCallback<typeof setSelectedDates>(
    (changes, updateType) => {
      setSelectedDates(changes, updateType);

      if (persist && changes && typeof changes !== "function") {
        writeStoredRange(operatorId, changes.dateFrom ?? undefined, changes.dateTo ?? undefined);
      }
    },
    [operatorId, persist, setSelectedDates]
  );

  /**
   * Fed back to `<DateRangePicker preset>`, which drives the input's label AND the granularity of its
   * prev/next arrows — so a restored "This Month" steps by month, not by day.
   */
  const preset = useMemo(() => getDatePreset(selectedDates.dateFrom, selectedDates.dateTo), [selectedDates.dateFrom, selectedDates.dateTo]);

  return [selectedDates, setDates, preset] as const;
};
